import { Collapse, Popover } from '@mui/material';
import { List, ListDivider, ListItem } from '@mui/joy';
import { useContext, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import useToggle from '@/shared/hooks/UseToggle';

import { AppContext } from '@/shared/context/context';

import SidebarItem, { SidebarFlyoutContext, type SidebarItemProps } from './SidebarItem';
import { AngleUpIcon } from '@/shared/icons/Icons';
import IconButton from '@/shared/components/buttons/IconButton';

type CollapsibleSidebarItemProps = SidebarItemProps &
  React.PropsWithChildren<{
    hideCollapse?: boolean;
  }>;

const FLYOUT_CLOSE_DELAY_MS = 150;

const CollapsibleSidebarItem: React.FC<CollapsibleSidebarItemProps> = ({
  children,
  hideCollapse = false,
  ...itemProps
}) => {
  const { ui } = useContext(AppContext);

  const toggle = useToggle();

  const [flyoutAnchor, setFlyoutAnchor] = useState<HTMLElement | null>(null);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const clearCloseTimeout = () => {
    if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
  };

  useEffect(() => clearCloseTimeout, []);

  const openFlyout = (event: { currentTarget: HTMLElement }) => {
    clearCloseTimeout();
    setFlyoutAnchor(event.currentTarget);
  };

  const scheduleFlyoutClose = () => {
    clearCloseTimeout();
    closeTimeoutRef.current = setTimeout(() => setFlyoutAnchor(null), FLYOUT_CLOSE_DELAY_MS);
  };

  if (!ui.toggles.mainMenu && !hideCollapse) {
    return (
      <>
        <SidebarItem
          {...itemProps}
          disableCollapsedTooltip
          onBlur={scheduleFlyoutClose}
          onFocus={openFlyout}
          onMouseEnter={openFlyout}
          onMouseLeave={scheduleFlyoutClose}
        />
        <Popover
          disableAutoFocus
          disableEnforceFocus
          disableRestoreFocus
          PaperProps={{
            className: classNames(
              'pointer-events-auto ml-2 min-w-[220px] overflow-hidden rounded-xl',
              'border border-slate-200 bg-white py-2 shadow-lg shadow-slate-900/10',
            ),
            elevation: 0,
            onMouseEnter: clearCloseTimeout,
            onMouseLeave: scheduleFlyoutClose,
          }}
          anchorEl={flyoutAnchor}
          anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
          open={!!flyoutAnchor}
          sx={{ pointerEvents: 'none' }}
          transformOrigin={{ horizontal: 'left', vertical: 'top' }}
          onClose={() => setFlyoutAnchor(null)}
        >
          <SidebarFlyoutContext.Provider value>
            <List size="sm">
              <SidebarItem {...itemProps} />
              <ListDivider className="my-1" />
              {children}
            </List>
          </SidebarFlyoutContext.Provider>
        </Popover>
      </>
    );
  }

  return (
    <ListItem
      nested
      endAction={
        ui.toggles.mainMenu && !hideCollapse ? (
          <IconButton
            className="ml-2 flex items-center"
            iconComponent={
              <AngleUpIcon
                className={classNames('mr-2 transition-all duration-150', {
                  'rotate-180': !toggle.value,
                })}
              />
            }
            onClick={toggle.inverse}
          />
        ) : null
      }
    >
      <List size="sm">
        <SidebarItem {...itemProps} />
        <Collapse unmountOnExit in={ui.toggles.mainMenu && toggle.value} timeout="auto">
          <List size="sm">{children}</List>
        </Collapse>
      </List>
    </ListItem>
  );
};

export default CollapsibleSidebarItem;
